import type { WorkerManager } from '../../worker';
import {
    createFeatureReplayBatchValidation,
    validateFeatureReplay,
    type FeatureReplayValidationResult,
} from '../rebuild/FeatureReplayConsistency';
import type {WorkerRevisionGuardState} from '../../history/worker';
import type {
    QueryConvertEntitiesFaceBoundariesPayload,
    QueryConvertEntitiesFaceBoundariesResult,
    QueryConvertEntitiesInnerLoopByEdgePayload,
    QueryConvertEntitiesInnerLoopByEdgeResult,
} from '../../../types/reference/sketch-entity-reference/TopologyFaceBoundaryRuntime';

/**
 * Feature replay 使用的批量执行器。
 */
interface FeatureReplayBatchExecutor {
    /**
     * 批量执行命令并返回 Worker 几何状态摘要。
     *
     * @param payload 批量执行载荷。
     * @returns 执行后的 Worker 几何状态摘要。
     */
    batchExecute(payload: BatchExecutePayload): Promise<WorkerGeometryStateSummary>;
}

/**
 * Feature replay 执行输入。
 */
export interface FeatureReplayExecutionInput {
    /** replay 使用的命令序列。 */
    readonly commands: CadCommand[];
    /** 是否在 replay 前清空 Worker 几何状态。 */
    readonly clearFirst?: boolean;
    /** 目标特征 ID；用于校验 featureRevision。 */
    readonly targetFeatureId?: string;
    /** 期望的 replay 结果哈希；checkpoint / crash recovery 可用。 */
    readonly expectedResultHash?: string;
    /** 历史快照中记录的期望命令序列哈希。 */
    readonly expectedCommandSeq?: string;
    /** 历史快照中记录的期望 feature revision。 */
    readonly expectedFeatureRevision?: string;
    /** 额外 Worker 批量执行校验项。 */
    readonly validation?: BatchExecuteValidation;
    /** replay 校验失败后用于回滚 Worker 派生状态的命令序列。 */
    readonly rollbackCommands?: CadCommand[];
    /** 是否把 replay 后的 Worker history 同步为完整特征历史投影。 */
    readonly syncHistoryProjection?: boolean;
}

/**
 * Feature replay 执行结果。
 */
export interface FeatureReplayExecutionResult {
    /** Worker replay 后的几何状态摘要。 */
    readonly summary: WorkerGeometryStateSummary;
    /** 主线程复核得到的一致性结果。 */
    readonly validation: FeatureReplayValidationResult;
}

/**
 * Worker 几何状态摘要读取选项。
 */
export interface FeatureGeometryStateSummaryReadOptions {
    /** 是否显式把读取到的 Worker history 同步为完整特征历史投影。 */
    readonly syncHistoryProjection?: boolean;
}

/**
 * 特征执行运行时。
 */
export class FeatureExecutionRuntime {
    /** Worker 管理器。 */
    private readonly workerManager: WorkerManager;
    /** 历史同步回调。 */
    private readonly syncHistoryProjection: (history: CadCommand[]) => void;

    /**
     * @param workerManager Worker 管理器。
     * @param syncHistoryProjection 历史同步回调。
     */
    public constructor(
        workerManager: WorkerManager,
        syncHistoryProjection: (history: CadCommand[]) => void,
        private readonly getDocumentScope: () => FeatureDocumentScope | null = () => null,
    ) {
        this.workerManager = workerManager;
        this.syncHistoryProjection = syncHistoryProjection;
    }

    /**
     * 执行单条命令。
     * @param command 目标命令。
     * @returns 执行结果。
     */
    public async executeCommand(command: CadCommand): Promise<Awaited<ReturnType<WorkerManager['executeCommand']>>> {
        const documentScope = this.getDocumentScope();
        // 所有新建特征共用此正式入口，避免零件中先旋转再贯穿时丢失归属。
        if (documentScope && !command.params.documentScopeNodeKey) Object.assign(command.params, documentScope);
        this.advanceWorkerMutationEpoch();
        const result = await this.workerManager.executeCommand(command);
        await this.syncProjectionFromWorker();
        return result;
    }

    /**
     * 批量回放命令。
     * @param payload 批量执行载荷。
     * @returns 执行后的几何状态摘要。
     */
    public async executeBatchCommands(payload: BatchExecutePayload): Promise<WorkerGeometryStateSummary> {
        this.advanceWorkerMutationEpoch();
        const state = await this.workerManager.batchExecute(payload);
        this.syncHistoryProjection(state.history);
        return state;
    }

    /**
     * 按 Feature 粒度执行当前时间线命令段。
     *
     * 该入口只推进 Worker 派生状态 epoch，不同步完整 FeatureManager 历史；
     * 调用方使用真实 request revision 回执判断后续 checkpoint 是否仍可提交。
     *
     * @param payload 成功前缀批执行载荷。
     * @returns Worker 执行结果与发请求前的真实 revision。
     */
    public async executeFeaturePrefixBatch(
        payload: ExecuteFeaturePrefixBatchPayload,
    ): Promise<FeaturePrefixBatchExecutionReceipt> {
        const requestRevision = this.advanceWorkerMutationEpoch();
        const result = await this.workerManager.executeFeaturePrefixBatch(payload);
        return {
            result,
            requestRevision,
        };
    }

    /**
     * 查询 active Worker 当前历史位置的轻量 topology checkpoint。
     *
     * @param payload topology owner 与时间线可见性命令。
     * @returns 命中的 owner topology 与缺失 owner key。
     */
    public queryTopologyCheckpoint(
        payload: QueryTopologyCheckpointPayload,
    ): Promise<QueryTopologyCheckpointResult> {
        return this.workerManager.queryTopologyCheckpoint(payload);
    }

    /**
     * 查询当前 Worker BRep 中指定 Face 的 Convert Entities Wire 边界。
     *
     * @param payload topology owner 与当前 Face index。
     * @returns 完整 outer/inner Wire 边索引。
     */
    public queryConvertEntitiesFaceBoundaries(
        payload: QueryConvertEntitiesFaceBoundariesPayload,
    ): Promise<QueryConvertEntitiesFaceBoundariesResult> {
        return this.workerManager.queryConvertEntitiesFaceBoundaries(payload);
    }

    /**
     * 查询当前 Worker BRep 中点击边所属的唯一直接 InnerWire。
     *
     * @param payload topology owner 与点击 edge index。
     * @returns 唯一 InnerWire；非内环边返回 null。
     */
    public queryConvertEntitiesInnerLoopByEdge(
        payload: QueryConvertEntitiesInnerLoopByEdgePayload,
    ): Promise<QueryConvertEntitiesInnerLoopByEdgeResult> {
        return this.workerManager.queryConvertEntitiesInnerLoopByEdge(payload);
    }

    /**
     * 以 Feature replay 语义执行命令历史。
     *
     * 设计说明：
     * 1. replay 只用于重建 Worker/BRep/Mesh 等派生状态，不代表提交新的模型历史；
     * 2. Worker 事务内先校验 commandSeq / featureRevision / resultHash，失败会回滚 Worker 几何状态；
     * 3. 主线程拿到结果后再次复核，只有一致时才同步历史投影并允许 Viewer 消费派生网格。
     *
     * @param input replay 输入。
     * @returns replay 执行结果。
     */
    public async replayFeatureCommands(input: FeatureReplayExecutionInput): Promise<FeatureReplayExecutionResult> {
        this.advanceWorkerMutationEpoch();
        return this.replayFeatureCommandsWithBatchExecutor(this.workerManager, input);
    }

    /**
     * 在文档 runtime switch 的 staged Worker runtime 中回放命令。
     *
     * 设计说明：
     * 1. 页面首次加载或完整文档替换时，replay 必须写入 staged runtime，不能触碰 active Worker；
     * 2. 这里仍复用 active replay 的 commandSeq / featureRevision / resultHash 校验，避免新增入口绕开一致性保护；
     * 3. 方法只负责 staged replay 与校验，是否 commit 或 abort 由 Viewer 文档级恢复入口统一收口。
     *
     * @param runtimeSwitch 文档 runtime switch 句柄。
     * @param input replay 输入。
     * @returns replay 执行结果。
     */
    public async replayFeatureCommandsInDocumentRuntimeSwitch(
        runtimeSwitch: FeatureReplayBatchExecutor,
        input: FeatureReplayExecutionInput,
    ): Promise<FeatureReplayExecutionResult> {
        this.advanceWorkerMutationEpoch();
        return this.replayFeatureCommandsWithBatchExecutor(runtimeSwitch, {
            ...input,
            // staged replay 尚未成为 active Worker 权威状态，不能提前同步 FeatureManager 历史投影。
            syncHistoryProjection: false,
        });
    }

    /**
     * 使用指定批量执行器回放命令并执行一致性校验。
     *
     * @param executor 批量执行器。
     * @param input replay 输入。
     * @returns replay 执行结果。
     */
    private async replayFeatureCommandsWithBatchExecutor(
        executor: FeatureReplayBatchExecutor,
        input: FeatureReplayExecutionInput,
    ): Promise<FeatureReplayExecutionResult> {
        const commands = input.commands.map((command) => structuredClone(command));
        const replayValidation = createFeatureReplayBatchValidation({
            commands,
            featureId: input.targetFeatureId,
            expectedResultHash: input.expectedResultHash,
            expectedCommandSeq: input.expectedCommandSeq,
            expectedFeatureRevision: input.expectedFeatureRevision,
        });
        const summary = await executor.batchExecute({
            commands,
            clearFirst: input.clearFirst,
            validation: {
                ...(input.validation ?? {}),
                ...replayValidation,
            },
        });
        const validation = validateFeatureReplay({
            commands,
            summary,
            featureId: input.targetFeatureId,
            expectedCommandSeq: replayValidation.expectedCommandSeq,
            expectedFeatureRevision: replayValidation.expectedFeatureRevision,
            expectedResultHash: replayValidation.expectedResultHash,
        });

        if (validation.status === 'invalid') {
            if (input.rollbackCommands) {
                const rollbackCommands = input.rollbackCommands.map((command) => structuredClone(command));
                await executor.batchExecute({
                    commands: rollbackCommands,
                    clearFirst: true,
                    validation: createFeatureReplayBatchValidation({
                        commands: rollbackCommands,
                    }),
                });
            }
            throw new Error(`Feature replay 一致性校验失败：${validation.reasons.join('; ')}`);
        }

        if (input.syncHistoryProjection === true) {
            this.syncHistoryProjection(summary.history);
        }

        return {
            summary,
            validation,
        };
    }

    /**
     * 读取当前几何状态摘要。
     * @returns 当前几何状态摘要。
     */
    public async getGeometryStateSummary(
        options: FeatureGeometryStateSummaryReadOptions = {},
    ): Promise<WorkerGeometryStateSummary> {
        const state = await this.workerManager.getState();
        if (options.syncHistoryProjection === true) {
            this.syncHistoryProjection(state.history);
        }
        return state;
    }

    /**
     * 显式从 Worker 当前状态同步一次特征历史投影。
     *
     * 设计说明：
     * 1. 普通几何摘要读取必须保持纯读，避免退回状态下的 active prefix 覆盖完整 FeatureManager 时间线；
     * 2. 需要让 Worker history 成为新权威的入口必须调用该方法，使 Undo/Redo、项目恢复与普通 UI 刷新边界清晰。
     *
     * @returns 同步使用的 Worker 几何状态摘要。
     */
    public async syncFeatureHistoryProjectionFromWorker(): Promise<WorkerGeometryStateSummary> {
        const state = await this.workerManager.getState();
        this.syncHistoryProjection(state.history);
        return state;
    }

    /**
     * 生成目标对象网格。
     * @param objectId 对象 ID。
     * @returns 网格数据。
     */
    public async generateMeshData(objectId: string): Promise<MeshData> {
        return this.workerManager.generateMesh(objectId);
    }

    /**
     * 释放已由特征重放接管的导入基线。
     *
     * 设计说明：离线包重开的显示交接必须同时收口场景侧与 Worker 侧——场景侧移除导入 GLB 显示，
     * Worker 侧移除被工作态遮蔽的同 id 基线；否则后续编辑/退回清空工作态时旧基线体会重新暴露形成双体。
     *
     * @param objectIds 由特征命令成功重放产出的实体 ID 列表。
     * @returns 释放数量与最新几何状态摘要。
     */
    public async releaseFeatureOwnedBaselines(
        objectIds: readonly string[],
    ): Promise<{releasedCount: number; summary: WorkerGeometryStateSummary}> {
        return this.workerManager.releaseFeatureOwnedBaselines({objectIds: [...objectIds]});
    }

    /**
     * 推进 Worker 派生状态变更 epoch。
     *
     * 设计说明：
     * Worker 中会改变几何状态的 execute / replay 必须先推进 historySeq。
     * 这样旧的 preview、mesh 或 rebuild 异步响应返回时，会被 WorkerRevisionGuard 拦截，
     * 不会在 undo/redo 或新 replay 之后覆盖当前 Viewer 状态。
     */
    private advanceWorkerMutationEpoch(): WorkerRevisionGuardState {
        return this.workerManager.advanceHistorySeq();
    }

    /**
     * 读取 Worker revision guard 状态。
     *
     * @returns Worker revision guard 状态。
     */
    public getWorkerRevisionState(): WorkerRevisionGuardState {
        return this.workerManager.getRevisionState();
    }

    /**
     * 设置 Worker revision 上下文。
     *
     * @param input revision 上下文。
     * @returns 更新后的 guard 状态。
     */
    public setWorkerRevisionContext(input: {
        documentRevision?: number;
        historySeq?: number;
    }): WorkerRevisionGuardState {
        return this.workerManager.setRevisionContext(input);
    }

    /**
     * 推进 Worker 文档 revision。
     *
     * @param documentRevision 指定 revision；不传则递增。
     * @returns 更新后的 guard 状态。
     */
    public advanceWorkerDocumentRevision(documentRevision?: number): WorkerRevisionGuardState {
        return this.workerManager.advanceDocumentRevision(documentRevision);
    }

    /**
     * 推进 Worker 历史序号。
     *
     * @param historySeq 指定历史序号；不传则递增。
     * @returns 更新后的 guard 状态。
     */
    public advanceWorkerHistorySeq(historySeq?: number): WorkerRevisionGuardState {
        return this.workerManager.advanceHistorySeq(historySeq);
    }

    /**
     * 从 Worker 同步一次历史投影。
     */
    private async syncProjectionFromWorker(): Promise<void> {
        const state = await this.workerManager.getState();
        this.syncHistoryProjection(state.history);
    }
}
